import * as THREE from 'three';
import { useGalaxyStore } from '../../core/state/GalaxyStore';
import { CameraController } from '../../visualization/camera/CameraController';

export interface KeyboardMapping {
  [key: string]: () => void;
}

export class KeyboardController {
  private cameraController: CameraController;
  private store: typeof useGalaxyStore;
  private scene: THREE.Scene;
  
  private isEnabled: boolean = false;
  private pressedKeys: Set<string> = new Set();
  
  // Movement settings
  private movementSpeed: number = 50; // units per second
  private fastMovementMultiplier: number = 3;
  
  // Key mappings
  private keyMappings: KeyboardMapping;
  
  // Event listeners (stored for cleanup)
  private boundKeyDown: EventListener;
  private boundKeyUp: EventListener;
  
  constructor(
    cameraController: CameraController,
    store: typeof useGalaxyStore,
    scene: THREE.Scene
  ) {
    this.cameraController = cameraController;
    this.store = store;
    this.scene = scene;
    
    this.setupKeyMappings();
    this.setupEventListeners();
  }
  
  private setupKeyMappings(): void {
    this.keyMappings = {
      // Camera presets (number keys)
      '1': () => this.setCameraPreset('overview'),
      '2': () => this.setCameraPreset('sunClose'),
      '3': () => this.setCameraPreset('mempoolFocus'),
      '4': () => this.setCameraPreset('blockOrbit'),
      
      // Camera modes
      'KeyF': () => this.toggleFollowMode(),
      'KeyC': () => this.cycleCameraMode(),
      
      // Time controls
      'Space': () => this.toggleTimeScale(),
      'Equal': () => this.increaseTimeScale(), // + key
      'Minus': () => this.decreaseTimeScale(),
      
      // UI controls
      'KeyH': () => this.toggleHUD(),
      'Escape': () => this.clearSelection(),
      
      // Free camera controls (handled in update loop)
      // WASD keys are checked in the update method
    };
  }
  
  private setupEventListeners(): void {
    this.boundKeyDown = (event: KeyboardEvent) => {
      if (!this.isEnabled) return;
      
      // Prevent default for keys we handle
      if (this.shouldPreventDefault(event.code)) {
        event.preventDefault();
      }
      
      // Add to pressed keys for continuous input (WASD)
      this.pressedKeys.add(event.code);
      
      // Handle single press actions
      const action = this.keyMappings[event.code];
      if (action) {
        action();
      }
    };
    
    this.boundKeyUp = (event: KeyboardEvent) => {
      if (!this.isEnabled) return;
      
      // Remove from pressed keys
      this.pressedKeys.delete(event.code);
    };
  }
  
  private shouldPreventDefault(code: string): boolean {
    // Prevent default for space (page scroll) and other keys we handle
    return ['Space', 'KeyF', 'KeyH', 'Escape'].includes(code) ||
           code.match(/^Digit[1-4]$/) !== null;
  }
  
  // Camera preset actions
  private setCameraPreset(preset: string): void {
    console.log(`Setting camera preset: ${preset}`);
    this.cameraController.setPreset(preset as keyof import('../../visualization/camera/CameraController').CameraPresets);
  }
  
  // Camera mode actions
  private toggleFollowMode(): void {
    const currentState = this.store.getState();
    const selectedObjectId = currentState.ui.selectedObject;
    
    if (!selectedObjectId) {
      console.log('No object selected for follow mode');
      return;
    }
    
    // Check if already following
    if (currentState.ui.cameraMode === 'follow' && this.cameraController.isFollowing()) {
      // Stop following
      this.cameraController.stopFollowing();
      this.store.getState().setCameraMode('orbit');
      console.log('Stopped following object');
      return;
    }
    
    // Find the selected object in the scene
    const selectedObject = this.findObjectById(selectedObjectId);
    
    if (selectedObject) {
      // Start following
      this.cameraController.followObject(selectedObject);
      this.store.getState().setCameraMode('follow');
      console.log(`Started following object: ${selectedObjectId}`);
    } else {
      console.log(`Selected object not found: ${selectedObjectId}`);
    }
  }

  private findObjectById(objectId: string): THREE.Object3D | null {
    let foundObject: THREE.Object3D | null = null;
    
    this.scene.traverse((object) => {
      if (object.userData?.['id'] === objectId) {
        foundObject = object;
      }
    });
    
    return foundObject;
  }
  
  private cycleCameraMode(): void {
    const currentMode = this.store.getState().ui.cameraMode;
    const modes = ['orbit', 'free', 'cinematic'] as const;
    const currentIndex = modes.indexOf(currentMode as any);
    const nextIndex = (currentIndex + 1) % modes.length;
    const nextMode = modes[nextIndex];
    
    this.store.getState().setCameraMode(nextMode);
    this.cameraController.setMode(nextMode);
    console.log(`Camera mode changed to: ${nextMode}`);
  }
  
  // Time control actions
  private toggleTimeScale(): void {
    const currentTimeScale = this.store.getState().ui.timeScale;
    const newTimeScale = currentTimeScale === 0 ? 1 : 0; // Toggle between pause and normal
    this.store.getState().setTimeScale(newTimeScale);
    console.log(`Time scale ${newTimeScale === 0 ? 'paused' : 'resumed'}: ${newTimeScale}`);
  }
  
  private increaseTimeScale(): void {
    const currentTimeScale = this.store.getState().ui.timeScale;
    const newTimeScale = Math.min(currentTimeScale * 1.5, 10);
    this.store.getState().setTimeScale(newTimeScale);
    console.log(`Time scale increased to: ${newTimeScale.toFixed(2)}`);
  }
  
  private decreaseTimeScale(): void {
    const currentTimeScale = this.store.getState().ui.timeScale;
    const newTimeScale = Math.max(currentTimeScale / 1.5, 0.1);
    this.store.getState().setTimeScale(newTimeScale);
    console.log(`Time scale decreased to: ${newTimeScale.toFixed(2)}`);
  }
  
  // UI actions
  private toggleHUD(): void {
    const currentSettings = this.store.getState().ui.settings;
    const newOpacity = currentSettings.hudOpacity > 0 ? 0 : 1;
    this.store.getState().updateSettings({ hudOpacity: newOpacity });
    console.log(`HUD ${newOpacity > 0 ? 'shown' : 'hidden'}`);
  }
  
  private clearSelection(): void {
    this.store.getState().selectObject(null);
    // Also stop following if we were following
    if (this.cameraController.isFollowing()) {
      this.cameraController.stopFollowing();
      this.store.getState().setCameraMode('orbit');
    }
    console.log('Selection cleared');
  }
  
  // Update method for continuous input (WASD movement)
  public update(deltaTime: number): void {
    if (!this.isEnabled) return;
    
    const currentMode = this.store.getState().ui.cameraMode;
    
    // Only handle WASD movement in free camera mode
    if (currentMode === 'free') {
      this.handleFreeCameraMovement(deltaTime);
    }
  }
  
  private handleFreeCameraMovement(deltaTime: number): void {
    const camera = this.cameraController.camera;
    const speed = this.movementSpeed * deltaTime;
    const fastSpeed = speed * this.fastMovementMultiplier;
    
    // Check for shift key for fast movement
    const isFastMovement = this.pressedKeys.has('ShiftLeft') || this.pressedKeys.has('ShiftRight');
    const currentSpeed = isFastMovement ? fastSpeed : speed;
    
    // Get camera direction vectors
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
    
    // Handle movement keys
    if (this.pressedKeys.has('KeyW')) {
      camera.position.addScaledVector(forward, currentSpeed);
    }
    if (this.pressedKeys.has('KeyS')) {
      camera.position.addScaledVector(forward, -currentSpeed);
    }
    if (this.pressedKeys.has('KeyA')) {
      camera.position.addScaledVector(right, -currentSpeed);
    }
    if (this.pressedKeys.has('KeyD')) {
      camera.position.addScaledVector(right, currentSpeed);
    }
    if (this.pressedKeys.has('KeyQ')) {
      camera.position.addScaledVector(up, -currentSpeed);
    }
    if (this.pressedKeys.has('KeyE')) {
      camera.position.addScaledVector(up, currentSpeed);
    }
  }
  
  public enable(): void {
    if (this.isEnabled) return;
    
    this.isEnabled = true;
    
    // Add event listeners to window for global keyboard handling
    window.addEventListener('keydown', this.boundKeyDown);
    window.addEventListener('keyup', this.boundKeyUp);
    
    console.log('KeyboardController enabled');
    console.log('Keyboard shortcuts:');
    console.log('  1-4: Camera presets');
    console.log('  F: Toggle follow mode');
    console.log('  C: Cycle camera modes');
    console.log('  Space: Pause/resume time');
    console.log('  +/-: Adjust time scale');
    console.log('  H: Toggle HUD');
    console.log('  Escape: Clear selection');
    console.log('  WASD+QE: Free camera movement (in free mode)');
    console.log('  Shift: Fast movement');
  }
  
  public disable(): void {
    if (!this.isEnabled) return;
    
    this.isEnabled = false;
    
    // Remove event listeners
    window.removeEventListener('keydown', this.boundKeyDown);
    window.removeEventListener('keyup', this.boundKeyUp);
    
    // Clear pressed keys
    this.pressedKeys.clear();
    
    console.log('KeyboardController disabled');
  }
  
  public dispose(): void {
    this.disable();
  }
}
